import React, { useState, useEffect } from 'react';
import { useLanguage } from '../Providers/LanguageContext';
import { CookieConsentService } from '../../services/cookieConsent/CookieConsentService';
import styles from './CookieConsent.module.css';

interface CookieConsentProps {
  onConsentChange?: (hasConsent: boolean) => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ onConsentChange }) => {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: true,
    advertising: true,
  });

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = CookieConsentService.hasConsent();
    setShowBanner(!hasConsent);

    if (hasConsent && onConsentChange) {
      onConsentChange(true);
    }
  }, [onConsentChange]);

  const handleAcceptAll = () => {
    CookieConsentService.acceptAll();
    setShowBanner(false);
    if (onConsentChange) onConsentChange(true);
  };

  const handleRejectAll = () => {
    CookieConsentService.rejectAll();
    setShowBanner(false);
    if (onConsentChange) onConsentChange(true);
  };

  const handleSavePreferences = () => {
    CookieConsentService.saveConsent({
      necessary: true,
      ...preferences,
    });
    setShowBanner(false);
    setShowCustomize(false);
    if (onConsentChange) onConsentChange(true);
  };

  const handleCustomize = () => {
    setShowCustomize(true);
  };

  const handleClose = () => {
    // Don't allow closing without making a choice
    // User must accept, reject, or customize
  };

  if (!showBanner) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.banner}>
        {!showCustomize ? (
          // Main banner
          <>
            <div className={styles.content}>
              <h3 className={styles.title}>
                🍪 {t('cookieConsentBanner.title')}
              </h3>
              <p className={styles.description}>
                {t('cookieConsentBanner.description')}
              </p>
              <div className={styles.links}>
                <a href="/privacy" className={styles.link} target="_blank" rel="noopener noreferrer">
                  {t('cookieConsentBanner.privacyPolicy')}
                </a>
                <span className={styles.separator}>•</span>
                <a href="/cookies" className={styles.link} target="_blank" rel="noopener noreferrer">
                  {t('cookieConsentBanner.cookiePolicy')}
                </a>
              </div>
            </div>

            <div className={styles.actions}>
              <button
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={handleRejectAll}
              >
                {t('cookieConsentBanner.rejectAll')}
              </button>
              <button
                className={`${styles.button} ${styles.buttonOutline}`}
                onClick={handleCustomize}
              >
                {t('cookieConsentBanner.customize')}
              </button>
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={handleAcceptAll}
              >
                {t('cookieConsentBanner.acceptAll')}
              </button>
            </div>
          </>
        ) : (
          // Customize view
          <>
            <div className={styles.content}>
              <h3 className={styles.title}>
                {t('cookieConsentBanner.customize')}
              </h3>

              <div className={styles.preferencesList}>
                {/* Necessary cookies - always required */}
                <div className={styles.preferenceItem}>
                  <div className={styles.preferenceHeader}>
                    <label className={styles.preferenceLabel}>
                      <input
                        type="checkbox"
                        checked={true}
                        disabled={true}
                        className={styles.checkbox}
                      />
                      <div className={styles.preferenceInfo}>
                        <strong>{t('cookieConsentBanner.necessary')}</strong>
                        <p className={styles.preferenceDesc}>
                          {t('cookieConsentBanner.necessaryDesc')}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className={styles.preferenceItem}>
                  <div className={styles.preferenceHeader}>
                    <label className={styles.preferenceLabel}>
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) =>
                          setPreferences({ ...preferences, analytics: e.target.checked })
                        }
                        className={styles.checkbox}
                      />
                      <div className={styles.preferenceInfo}>
                        <strong>{t('cookieConsentBanner.analytics')}</strong>
                        <p className={styles.preferenceDesc}>
                          {t('cookieConsentBanner.analyticsDesc')}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Advertising cookies */}
                <div className={styles.preferenceItem}>
                  <div className={styles.preferenceHeader}>
                    <label className={styles.preferenceLabel}>
                      <input
                        type="checkbox"
                        checked={preferences.advertising}
                        onChange={(e) =>
                          setPreferences({ ...preferences, advertising: e.target.checked })
                        }
                        className={styles.checkbox}
                      />
                      <div className={styles.preferenceInfo}>
                        <strong>{t('cookieConsentBanner.advertising')}</strong>
                        <p className={styles.preferenceDesc}>
                          {t('cookieConsentBanner.advertisingDesc')}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={() => setShowCustomize(false)}
              >
                {t('cookieConsentBanner.back')}
              </button>
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={handleSavePreferences}
              >
                {t('cookieConsentBanner.savePreferences')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
